import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type SegmentService from 'ember-cli-segment/services/segment';
import type { InviteRouteModel } from 'invite-sign/routes/invite';
import type LocalizationService from 'invite-sign/services/localization';
import type { AvailableLocales } from 'invite-sign/utils/date-fns-i18n';
import LogRocket from 'logrocket';

export type ApplicationRouteModel = {
  embedded?: ApplicationRouteParams['embedded'];
};

export type ApplicationRouteParams = {
  embedded: string;
  lang: AvailableLocales;
};

export default class ApplicationRoute extends Route {
  @service declare router: RouterService;
  @service declare segment: SegmentService;
  @service declare localization: LocalizationService;

  queryParams = {
    lang: {
      refreshModel: true,
    },
  };

  async beforeModel(transition: Transition): Promise<void> {
    await super.beforeModel(transition);

    const { lang } = <ApplicationRouteParams>this.paramsFor('application');

    await this.localization.loadLocaleTask.perform(lang || 'en');
  }

  model(params: ApplicationRouteParams): ApplicationRouteModel {
    return {
      embedded: params.embedded,
    };
  }

  identifyUser(): void {
    // Invites path
    const inviteModel = <InviteRouteModel>this.modelFor('invite');
    if (inviteModel) {
      const { invite } = inviteModel;
      if (invite && invite.id) {
        this.segment.identifyUser(invite.id, { invite });
        LogRocket.identify(invite.id, { inviteId: invite.id });
      }
    }
  }

  trackPageView(): void {
    // Invites path
    const inviteModel = <InviteRouteModel>this.modelFor('invite');
    if (inviteModel) {
      const { invite } = inviteModel;

      if (invite && invite.id) {
        const currentRouteName = this.router.currentRouteName ?? 'unknown';
        let path = window.location.pathname.replace(`/${invite.id}`, '');
        // Prevent firing multiple different events when viewing agreements
        path = path.replace(/\/agreement\/[0-9]+/, '/agreement');
        const inviteJson = <{ photo?: string }>invite.toJSON();
        delete inviteJson.photo; // Do not send base64 image to segment
        this.segment.trackPageView(currentRouteName, { invite: inviteJson, path });
      }
    }
  }
}
